// Wave I — Platform super-admin login page (`/super/login`).
//
// Public route. The keys to the kingdom: a successful login returns a
// realm='super' JWT minted from /api/v1/auth/super/login. The token is
// stored in the same `accessToken` slot as the tenant flow, with
// `realm='super'` in storage so the axios interceptor knows to refresh
// against /auth/super/refresh.
//
// Visually deliberately distinct from /login — a Shield icon in the brand
// area, "Platform Admin" subtitle, and a darker accent gradient — so an
// operator can never confuse the two pages.

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, LogIn, Shield } from 'lucide-react';
import axios from 'axios';
import { useSuperAuth } from '../../../auth/useSuperAuth';
import styles from './SuperLoginPage.module.css';

const superLoginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});
type SuperLoginFormValues = z.infer<typeof superLoginSchema>;

export function SuperLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useSuperAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SuperLoginFormValues>({
    resolver: zodResolver(superLoginSchema),
    defaultValues: { username: '', password: '' },
  });

  // Already logged in? Bounce to the redirect target (or /super).
  useEffect(() => {
    if (isAuthenticated) {
      const target = decodeURIComponent(searchParams.get('redirect') ?? '/super');
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, navigate, searchParams]);

  const onSubmit = async (values: SuperLoginFormValues) => {
    setSubmitError(null);
    try {
      await login({ username: values.username, password: values.password });
      const target = decodeURIComponent(searchParams.get('redirect') ?? '/super');
      navigate(target, { replace: true });
    } catch (err: unknown) {
      setSubmitError(extractErrorMessage(err));
    }
  };

  return (
    <div className={styles.shell}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <div className={styles.brandIcon} aria-hidden>
            <Shield size={28} />
          </div>
          <div className={styles.brandMark}>Tricivo</div>
          <div className={styles.brandSub}>Platform Admin</div>
        </div>

        <h1 className={styles.heading}>Sign in to admin</h1>
        <p className={styles.lead}>Tricivo platform-administrator access.</p>

        {submitError ? (
          <div className={styles.errorBanner} role="alert">
            <AlertCircle size={16} aria-hidden />
            <span>{submitError}</span>
          </div>
        ) : null}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className={styles.field}>
            <label htmlFor="su-username" className={styles.label}>
              Username
            </label>
            <input
              id="su-username"
              type="text"
              autoComplete="username"
              autoFocus
              className={`${styles.input} ${errors.username ? styles.invalid : ''}`}
              {...register('username')}
            />
            {errors.username ? (
              <div className={styles.fieldError}>{errors.username.message}</div>
            ) : null}
          </div>

          <div className={styles.field}>
            <label htmlFor="su-password" className={styles.label}>
              Password
            </label>
            <input
              id="su-password"
              type="password"
              autoComplete="current-password"
              className={`${styles.input} ${errors.password ? styles.invalid : ''}`}
              {...register('password')}
            />
            {errors.password ? (
              <div className={styles.fieldError}>{errors.password.message}</div>
            ) : null}
          </div>

          <button type="submit" className={styles.submit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className={styles.spinner} aria-hidden />
                Signing in…
              </>
            ) : (
              <>
                <LogIn size={16} aria-hidden />
                Sign in
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    if (status === 401) return 'Invalid credentials.';
    if (status === 429) return 'Too many attempts. Please wait and try again.';
    const body = err.response?.data as { message?: string } | undefined;
    if (body?.message) return body.message;
    if (err.message === 'Network Error') return 'Cannot reach the server. Check your connection.';
  }
  return 'Sign in failed. Please try again.';
}
